import React, { useState, useEffect } from 'react';
import { Star, Loader, Copy, Share2, Package, Users, Target, ShoppingBag, Clock, User, Phone, MapPin, Edit3, Save, X, ChevronRight, LogOut } from 'lucide-react';

// Inline keyframe for tree animation
const treeAnimStyle = document.createElement('style');
if (!document.getElementById('tree-anim-style')) {
  treeAnimStyle.id = 'tree-anim-style';
  treeAnimStyle.textContent = `@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`;
  document.head.appendChild(treeAnimStyle);
}
import { logger } from './utils/logger.js';
import haptics from './utils/haptics.js';
import botAPI from './botAPI.js';
import AuthManager from './utils/authManager';
import GlobalHeader from './components/GlobalHeader';
import OrderDetailModal from './components/OrderDetailModal';
import { Platform } from './utils/platform';
import { pluralizeUnit } from './utils';

// Склонение слова "заказ"
const pluralizeOrders = (n) => {
  const abs = Math.abs(n) % 100;
  const lastDigit = abs % 10;
  if (abs > 10 && abs < 20) return 'заказов';
  if (lastDigit === 1) return 'заказ';
  if (lastDigit >= 2 && lastDigit <= 4) return 'заказа';
  return 'заказов';
};

// Склонение слова "друг"
const pluralizeFriends = (n) => {
  const abs = Math.abs(n) % 100;
  const lastDigit = abs % 10;
  if (abs > 10 && abs < 20) return 'друзей';
  if (lastDigit === 1) return 'друга';
  if (lastDigit >= 2 && lastDigit <= 4) return 'друзей';
  return 'друзей';
};

/**
 * Личный кабинет пользователя (ProfilePage)
 *
 * Отображает 3 состояния:
 * 1. Обычный пользователь: баланс бонусов + история + кнопка "Подать заявку на организаторство"
 * 2. Pending (wants_to_be_organizer=true): статус заявки "На рассмотрении"
 * 3. Организатор (is_organizer=true): баланс + история + секция "Пригласи друзей"
 */
const ProfilePage = ({ telegramUser, onClose, currentPage = 'profile', onNavigate, cartItemsCount = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);

  // Состояние процесса становления организатором
  const [becomingOrganizer, setBecomingOrganizer] = useState(false);

  // Состояние заказов
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Совместные покупки
  const [packShareInterests, setPackShareInterests] = useState([]);

  // Состояние модалки деталей заказа
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  // Состояние редактирования контактов
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    delivery_address: '',
    delivery_entrance: '',
    delivery_floor: '',
    delivery_apartment: '',
    delivery_comment: '',
  });

  // Загрузка данных профиля
  useEffect(() => {
    if (!telegramUser?.id) {
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        setLoading(true);
        const isLoggedIn = !!AuthManager.getAccessToken();
        const [response, ordersResponse, packShareResponse] = await Promise.all([
          botAPI.getUserProfile(telegramUser.id),
          botAPI.getUserOrders().catch(() => null),
          isLoggedIn ? botAPI.getMyPackShareInterests().catch(() => ({ interests: [] })) : { interests: [] },
        ]);

        if (response?.success) {
          setProfileData(response);
          if (response?.user) {
            setContactForm({
              first_name: response.user.first_name || '',
              last_name: response.user.last_name || '',
              phone: response.user.phone || '',
              delivery_address: response.user.delivery_address || '',
              delivery_entrance: response.user.delivery_entrance || '',
              delivery_floor: response.user.delivery_floor || '',
              delivery_apartment: response.user.delivery_apartment || '',
              delivery_comment: response.user.delivery_comment || '',
            });
          }
          logger.log('✅ Профиль загружен:', response);
        } else {
          // Читаем реальное поле error (не .message, которого нет) + различаем 401
          if (response?.status === 401) {
            setError('Сессия истекла. Войдите заново, чтобы открыть профиль.');
          } else {
            setError(response?.error || 'Не удалось загрузить профиль');
          }
        }

        if (ordersResponse?.success) {
          setOrders(ordersResponse.orders || []);
        }
        if (packShareResponse?.interests) {
          setPackShareInterests(packShareResponse.interests);
        }
        // Cache referral code for sharing in other components
        if (response?.invitations?.referral_code) {
          try { sessionStorage.setItem('user_referral_code', response.invitations.referral_code); } catch {}
        }
      } catch (err) {
        console.error('❌ Ошибка загрузки профиля:', err);
        setError('Ошибка подключения к серверу');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [telegramUser?.id]);

  // Обработчик "Копировать ссылку"
  const handleCopyLink = () => {
    if (!profileData?.invitations?.referral_link) return;

    try {
      // Копируем в буфер обмена
      navigator.clipboard.writeText(profileData.invitations.referral_link);
      haptics.notification('success');
      alert('Ссылка скопирована!');
    } catch (err) {
      console.error('Ошибка копирования:', err);
      alert('Не удалось скопировать ссылку');
    }
  };

  // Обработчик "Поделиться"
  const handleShareLink = () => {
    if (!profileData?.invitations?.referral_link) return;

    try {
      haptics.impact('light');

      const shareUrl = profileData.invitations.referral_link;
      const shareText = 'Приглашаю заказывать свежие морепродукты через "Путь рыбы"! Переходи и получи приветственный бонус';

      if (navigator.share) {
        navigator.share({ title: 'Путь рыбы', text: shareText, url: shareUrl }).catch(() => {});
      } else {
        navigator.clipboard.writeText(shareUrl);
        alert('Ссылка скопирована!');
      }
    } catch (err) {
      console.error('Ошибка share:', err);
    }
  };

  // Обработчик "Стать организатором" - сразу даём доступ
  const handleBecomeOrganizer = async () => {
    try {
      setBecomingOrganizer(true);
      haptics.impact('medium');

      // Вызываем API для установки is_organizer = true
      const response = await botAPI.becomeOrganizer(telegramUser.id);

      if (response.success) {
        // Успешно - показываем уведомление и обновляем состояние
        alert('Поздравляем! Теперь вы организатор. Приглашайте друзей и получайте бонусы!');

        // Обновляем локальное состояние из ответа сервера
        if (response.user) {
          setProfileData({
            ...profileData,
            user: response.user
          });
        }

        haptics.notification('success');
      } else {
        // Ошибка от API
        throw new Error(response.error || 'Не удалось активировать статус');
      }
    } catch (err) {
      console.error('Ошибка при становлении организатором:', err);
      alert('Не удалось активировать статус организатора. Попробуйте позже.');
      haptics.notification('error');
    } finally {
      setBecomingOrganizer(false);
    }
  };

  // Форматирование телефона для отображения
  const formatPhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+7 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9)}`;
    }
    return phone;
  };

  // Авто-форматирование телефона при вводе
  const handlePhoneInput = (e) => {
    let digits = e.target.value.replace(/\D/g, '');
    // Если начинает с 8 — заменяем на 7
    if (digits.startsWith('8')) digits = '7' + digits.slice(1);
    // Если без 7 — добавляем
    if (digits.length > 0 && !digits.startsWith('7')) digits = '7' + digits;
    // Ограничиваем 11 цифрами
    digits = digits.slice(0, 11);

    let formatted = '';
    if (digits.length > 0) formatted = '+' + digits[0];
    if (digits.length > 1) formatted += ' (' + digits.slice(1, 4);
    if (digits.length > 4) formatted += ') ' + digits.slice(4, 7);
    if (digits.length > 7) formatted += '-' + digits.slice(7, 9);
    if (digits.length > 9) formatted += '-' + digits.slice(9, 11);

    setContactForm({ ...contactForm, phone: formatted });
  };

  // Сохранение контактных данных
  const handleSaveContact = async () => {
    try {
      setSaving(true);
      const response = await botAPI.updateProfile(telegramUser.id, contactForm);
      if (response?.success) {
        setEditMode(false);
        haptics.notification('success');
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      alert('Не удалось сохранить. Попробуйте позже.');
    } finally {
      setSaving(false);
    }
  };

  // Отмена редактирования — возврат к данным из profileData
  const handleCancelEdit = () => {
    if (profileData?.user) {
      setContactForm({
        first_name: profileData.user.first_name || '',
        last_name: profileData.user.last_name || '',
        phone: profileData.user.phone || '',
        delivery_address: profileData.user.delivery_address || '',
        delivery_entrance: profileData.user.delivery_entrance || '',
        delivery_floor: profileData.user.delivery_floor || '',
        delivery_apartment: profileData.user.delivery_apartment || '',
        delivery_comment: profileData.user.delivery_comment || '',
      });
    }
    setEditMode(false);
  };

  // Состояние загрузки
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
          <div className="text-gray-600 dark:text-gray-400">Загрузка профиля...</div>
        </div>
      </div>
    );
  }

  // Гостевой режим — предложение авторизации
  if (!telegramUser?.id) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 xl:pb-0">
        <GlobalHeader
          location="Москва и МО"
          currentPage={currentPage}
          onNavigate={onNavigate}
          cartItemsCount={cartItemsCount}
        />
        <div className="flex flex-col items-center justify-center px-4 pt-20">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
            <img src={new URL('./assets/icons/fish-logo.png', import.meta.url).href} alt="" className="w-14 h-14 mx-auto mb-4 object-contain" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Личный кабинет</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
              Войдите, чтобы видеть бонусы, историю заказов и управлять профилем
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('guest_mode');
                if (onNavigate) {
                  onNavigate('login');
                } else {
                  window.location.href = '/login';
                }
              }}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 px-4 rounded-xl transition-colors cursor-pointer mb-3"
            >
              Войти или зарегистрироваться
            </button>
            <button
              onClick={onClose}
              className="w-full text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 py-2 transition-colors"
            >
              Вернуться в каталог
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Состояние ошибки
  if (error || !profileData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-center" role="alert" aria-live="assertive">
          <div className="text-4xl" aria-hidden="true">⚠️</div>
          <div className="text-red-600 dark:text-red-400 max-w-xs">{error || 'Не удалось загрузить профиль'}</div>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              Повторить
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg"
            >
              Назад
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { user, bonuses, invitations, recent_transactions } = profileData;
  const isOrganizer = user?.is_organizer;
  const wantsToBeOrganizer = user?.wants_to_be_organizer;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 xl:pb-0">
      {/* Глобальный header */}
      <GlobalHeader
        location="Москва и МО"
        currentPage={currentPage}
        onNavigate={onNavigate}
        cartItemsCount={cartItemsCount}
      />

      {/* Заголовок */}
      <div className="px-4 mt-4 mb-6 xl:max-w-[1200px] xl:mx-auto xl:px-6">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 text-center">Личный кабинет</h1>
      </div>

      <div className="px-4 xl:max-w-[1200px] xl:mx-auto xl:px-6">
      {/* Секция "Контактные данные" */}
      <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/60 rounded-2xl p-4 mb-4 border border-gray-100 dark:border-white/10 shadow-sm"
        style={{ WebkitBackdropFilter: 'blur(16px)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <User size={22} className="text-sky-600 dark:text-sky-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Контактные данные</h2>
          </div>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="flex items-center gap-1 text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors"
            >
              <Edit3 size={14} />
              Редактировать
            </button>
          )}
        </div>

        {!editMode ? (
          /* VIEW MODE */
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400/20 to-cyan-500/10 flex items-center justify-center shrink-0">
                <User size={16} className="text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Имя</div>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {contactForm.first_name || contactForm.last_name
                    ? `${contactForm.first_name} ${contactForm.last_name}`.trim()
                    : <span className="text-gray-400 dark:text-gray-500">Не указано</span>
                  }
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400/20 to-cyan-500/10 flex items-center justify-center shrink-0">
                <Phone size={16} className="text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Телефон</div>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {contactForm.phone
                    ? formatPhone(contactForm.phone)
                    : <span className="text-gray-400 dark:text-gray-500">Не указано</span>
                  }
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400/20 to-cyan-500/10 flex items-center justify-center shrink-0">
                <MapPin size={16} className="text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Адрес доставки</div>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {contactForm.delivery_address
                    ? contactForm.delivery_address
                    : <span className="text-gray-400 dark:text-gray-500">Не указано</span>
                  }
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* EDIT MODE */
          <div className="space-y-3">
            {/* Имя */}
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Имя</label>
              <input
                type="text"
                value={contactForm.first_name}
                onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                placeholder="Иван"
                className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
              />
            </div>

            {/* Фамилия */}
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Фамилия</label>
              <input
                type="text"
                value={contactForm.last_name}
                onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                placeholder="Иванов"
                className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
              />
            </div>

            {/* Телефон */}
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Телефон</label>
              <input
                type="tel"
                value={contactForm.phone}
                onChange={handlePhoneInput}
                placeholder="+7 (999) 123-45-67"
                className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
              />
            </div>

            {/* Адрес доставки */}
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Адрес доставки</label>
              <input
                type="text"
                value={contactForm.delivery_address}
                onChange={(e) => setContactForm({ ...contactForm, delivery_address: e.target.value })}
                placeholder="ул. Примерная, д. 1"
                className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
              />
            </div>

            {/* Подъезд / Этаж / Квартира */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Подъезд</label>
                <input
                  type="text"
                  value={contactForm.delivery_entrance}
                  onChange={(e) => setContactForm({ ...contactForm, delivery_entrance: e.target.value })}
                  placeholder="1"
                  className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Этаж</label>
                <input
                  type="text"
                  value={contactForm.delivery_floor}
                  onChange={(e) => setContactForm({ ...contactForm, delivery_floor: e.target.value })}
                  placeholder="5"
                  className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Квартира</label>
                <input
                  type="text"
                  value={contactForm.delivery_apartment}
                  onChange={(e) => setContactForm({ ...contactForm, delivery_apartment: e.target.value })}
                  placeholder="42"
                  className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all"
                />
              </div>
            </div>

            {/* Комментарий к доставке */}
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Комментарий к доставке</label>
              <textarea
                value={contactForm.delivery_comment}
                onChange={(e) => setContactForm({ ...contactForm, delivery_comment: e.target.value })}
                placeholder="Код домофона, ориентиры..."
                rows={2}
                className="w-full px-3 py-2.5 bg-white/10 dark:bg-gray-700/50 border border-gray-200/30 dark:border-gray-600/30 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition-all resize-none"
              />
            </div>

            {/* Кнопки Сохранить / Отмена */}
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSaveContact}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition-colors"
              >
                {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white/10 hover:bg-white/20 dark:bg-gray-700/50 dark:hover:bg-gray-700/70 text-gray-700 dark:text-gray-300 font-semibold text-sm rounded-xl transition-colors border border-gray-200/30 dark:border-gray-600/30"
              >
                <X size={16} />
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Секция "Путь клиента" — бонусы + реферальное дерево */}
      <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/60 rounded-2xl p-4 mb-4 border border-gray-100 dark:border-white/10 shadow-sm"
        style={{ WebkitBackdropFilter: 'blur(16px)' }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Target size={22} className="text-sky-600 dark:text-sky-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Путь клиента</h2>
        </div>

        {/* Бонусы — компактные карточки */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <div className="text-center p-2.5 rounded-xl bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-900/20 dark:to-cyan-900/20 border border-sky-100/50 dark:border-sky-800/30">
            <div className="text-lg font-bold text-sky-600 dark:text-sky-400">
              {bonuses?.current_balance?.toLocaleString('ru-RU') || 0}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">баланс, руб</div>
          </div>
          <div className="text-center p-2.5 rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border border-emerald-100/50 dark:border-emerald-800/30">
            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
              +{bonuses?.lifetime_earnings?.toLocaleString('ru-RU') || 0}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">заработано, руб</div>
          </div>
          <div className="text-center p-2.5 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-100/50 dark:border-amber-800/30">
            <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
              {bonuses?.orders_count || 0}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{pluralizeOrders(bonuses?.orders_count || 0)}</div>
          </div>
        </div>

        {/* Реферальное дерево */}
        <div className="flex flex-col items-center py-1">

          {/* Кто тебя привёл (верхний уровень) — только если есть */}
          {invitations?.referred_by_user_id && (
            <>
              <div className="flex flex-col items-center" style={{ animation: 'fadeIn 0.5s ease' }}>
                <div className="relative w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(148,163,184,0.6), rgba(100,116,139,0.5))',
                    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(100,116,139,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  <span className="text-sm font-bold text-white opacity-80" style={{ filter: 'blur(0.3px)', textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>?</span>
                  <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.15), transparent 60%)' }} />
                </div>
                <div className="text-[10px] text-gray-400 mt-1">пригласил вас</div>
              </div>
              <div className="w-px h-4 bg-gradient-to-b from-gray-300 to-sky-400 dark:from-gray-600 dark:to-sky-500" />
            </>
          )}

          {/* ТЫ (центральный узел) — стеклянная аватарка */}
          <div className="flex flex-col items-center" style={{ animation: 'fadeIn 0.3s ease' }}>
            <div className="relative w-14 h-14 rounded-full flex items-center justify-center overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(14,165,233,0.8), rgba(6,182,212,0.75))',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                boxShadow: '0 6px 20px rgba(14,165,233,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <span className="text-xl font-bold text-white opacity-85" style={{ filter: 'blur(0.3px)', textShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>
                {(user?.first_name || 'Я')[0].toUpperCase()}
              </span>
              <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%)' }} />
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1.5">
              {user?.first_name || 'Вы'}
            </div>
          </div>

          {/* Ветви вниз к приглашённым */}
          {invitations?.invited_users?.length > 0 ? (
            <>
              <div className="w-px h-4 bg-gradient-to-b from-sky-400 to-sky-300 dark:from-sky-500 dark:to-sky-600" />

              <div className="relative w-full">
                {invitations.invited_users.length > 1 && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px bg-sky-300/60 dark:bg-sky-600/60"
                    style={{ width: `${Math.min(85, invitations.invited_users.slice(0, 5).length * 17)}%` }}
                  />
                )}

                <div className="flex justify-center gap-4 flex-wrap pt-0">
                  {invitations.invited_users.slice(0, 5).map((u, i) => {
                    const colors = [
                      'rgba(14,165,233,0.75), rgba(56,189,248,0.7)',
                      'rgba(16,185,129,0.75), rgba(52,211,153,0.7)',
                      'rgba(249,115,22,0.75), rgba(251,146,60,0.7)',
                      'rgba(139,92,246,0.75), rgba(167,139,250,0.7)',
                      'rgba(236,72,153,0.75), rgba(244,114,182,0.7)',
                    ];
                    return (
                      <div key={u.user_id} className="flex flex-col items-center"
                        style={{ animation: `fadeIn 0.4s ease ${0.1 * (i + 1)}s both` }}
                      >
                        <div className="w-px h-4 bg-sky-300/60 dark:bg-sky-600/60" />
                        <div className="relative w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                          style={{
                            background: `linear-gradient(135deg, ${colors[i % colors.length]})`,
                            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                            boxShadow: `0 4px 12px rgba(14,165,233,0.2), inset 0 1px 0 rgba(255,255,255,0.3)`,
                          }}
                        >
                          <span className="text-sm font-bold text-white opacity-80" style={{ filter: 'blur(0.3px)', textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
                            {(u.first_name || '?')[0].toUpperCase()}
                          </span>
                          <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.15), transparent 60%)' }} />
                        </div>
                        <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1 max-w-[56px] truncate text-center">
                          {u.first_name}
                        </div>
                      </div>
                    );
                  })}
                  {invitations.invited_users.length > 5 && (
                    <div className="flex flex-col items-center" style={{ animation: `fadeIn 0.4s ease 0.6s both` }}>
                      <div className="w-px h-4 bg-sky-300/60 dark:bg-sky-600/60" />
                      <div className="w-10 h-10 rounded-full bg-gray-100/80 dark:bg-gray-700/60 border border-gray-200/50 dark:border-gray-600/30 flex items-center justify-center text-xs font-medium text-gray-500 backdrop-blur-sm">
                        +{invitations.invited_users.length - 5}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                +{(invitations?.total_earned_from_referrals || 0).toLocaleString('ru-RU')} руб бонусов от друзей
              </div>
            </>
          ) : (
            <>
              <div className="w-px h-4 bg-gradient-to-b from-sky-400/60 to-gray-200 dark:from-sky-500/40 dark:to-gray-700" />
              <div className="flex gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col items-center opacity-25">
                    <div className="w-px h-3 bg-gray-300 dark:bg-gray-600" />
                    <div className="w-9 h-9 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600" />
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
                Пригласите друзей - бонус 5% от их заказов
              </div>
            </>
          )}
        </div>

        {/* Кнопки приглашения */}
        {invitations && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={handleCopyLink}
              className="py-2.5 bg-sky-50 dark:bg-sky-900/20 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-xl text-sm font-medium text-sky-700 dark:text-sky-400 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Copy size={14} /> Копировать ссылку
            </button>
            <button
              onClick={handleShareLink}
              className="py-2.5 bg-sky-50 dark:bg-sky-900/20 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-xl text-sm font-medium text-sky-700 dark:text-sky-400 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Share2 size={14} /> Поделиться
            </button>
          </div>
        )}
      </div>

      {/* Секция "Совместные покупки" */}
      {packShareInterests.length > 0 && (
        <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/60 rounded-2xl p-4 mb-4 border border-gray-100 dark:border-white/10 shadow-sm"
          style={{ WebkitBackdropFilter: 'blur(16px)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Users size={22} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Совместные покупки</h2>
          </div>

          <div className="space-y-2">
            {packShareInterests.map((interest) => (
              <div
                key={interest.interest_id}
                className="p-3.5 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-100 dark:border-gray-700/40"
              >
                <div className="flex items-start gap-3">
                  {interest.image_url ? (
                    <img src={interest.image_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Package size={20} className="text-blue-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {interest.product_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {interest.desired_quantity} {pluralizeUnit(interest.desired_quantity, interest.unit)} · {Math.round(interest.cycle_price * interest.desired_quantity).toLocaleString('ru-RU')} ₽
                      {interest.is_filled
                        ? <span className="text-green-600 dark:text-green-400 font-medium"> · скидка {interest.bonus_percent}% применена</span>
                        : <span className="italic"> · скидка {interest.bonus_percent}% если наберётся</span>
                      }
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2">
                      <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${interest.is_filled ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.min(100, interest.percent)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {interest.total_collected}/{interest.pack_size} {pluralizeUnit(interest.pack_size, interest.unit)} · {interest.total_participants} чел.
                        </span>
                      </div>
                    </div>

                    {/* Status */}
                    <div className={`mt-2 text-xs px-2.5 py-1 rounded-full font-medium inline-block ${
                      interest.is_filled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {interest.status_label}
                    </div>

                    {interest.is_filled && (
                      <button
                        className="mt-2 w-full py-2 text-sm font-semibold text-white rounded-lg"
                        style={{ background: 'linear-gradient(135deg, #0EA5E9, #6366F1)' }}
                        onClick={() => onNavigate && onNavigate('catalog')}
                      >
                        Оформить заказ
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Секция "Мои заказы" */}
      <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/60 rounded-2xl p-4 mb-4 border border-gray-100 dark:border-white/10 shadow-sm"
        style={{ WebkitBackdropFilter: 'blur(16px)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag size={22} className="text-amber-600 dark:text-amber-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Мои заказы</h2>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            Пока нет заказов
          </div>
        ) : (
          <div className="space-y-2">
            {orders.slice(0, 10).map((order) => {
              const statusConfig = {
                pending: { label: 'Ожидает', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
                paid: { label: 'Оплачен', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
                confirmed: { label: 'Подтверждён', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
                completed: { label: 'Выполнен', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
                cancelled: { label: 'Отменён', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
              };
              const status = statusConfig[order.order_status] || { label: order.order_status, color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' };

              return (
                <div
                  key={order.cycle_order_id}
                  className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-700/30 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 active:scale-[0.98] transition-all border border-gray-100 dark:border-gray-700/40"
                  onClick={() => { setSelectedOrder(order); setOrderModalOpen(true); Platform.hapticFeedback('light'); }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        №{order.cycle_order_id}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
                        {status.label}
                      </span>
                      {order.order_status === 'pending' && order.cycle_status === 'active' && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">
                          Изменить
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Clock size={11} />
                      {new Date(order.created_at).toLocaleDateString('ru-RU')}
                      {order.cycle_name && <span className="ml-1">· {order.cycle_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      {order.total_amount?.toLocaleString('ru-RU')} ₽
                    </div>
                    <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* История операций (последние 10) */}
      <div className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/60 rounded-2xl p-4 mb-4 border border-gray-100 dark:border-white/10 shadow-sm"
        style={{ WebkitBackdropFilter: 'blur(16px)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">История операций</h2>
        </div>

        {!recent_transactions || recent_transactions.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            Пока нет операций
          </div>
        ) : (
          <div className="space-y-2">
            {recent_transactions.slice(0, 10).map((tx) => (
              <div
                key={tx.transaction_id}
                className="flex items-center justify-between p-2 bg-white/[0.05] rounded-lg"
              >
                <div className="flex-1">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {tx.description || 'Операция'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(tx.created_at).toLocaleDateString('ru-RU')}
                  </div>
                </div>
                <div className={`text-base font-semibold ${tx.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString('ru-RU')} ₽
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Кнопка "Стать организатором" (только для обычных пользователей) */}
      {!isOrganizer && (
        <button
          onClick={handleBecomeOrganizer}
          disabled={becomingOrganizer}
          className="w-full py-4 backdrop-blur-xl bg-white/60 dark:bg-gray-800/40 rounded-2xl border border-white/50 dark:border-white/10 shadow-sm transition-all duration-300 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          style={{ WebkitBackdropFilter: 'blur(16px)' }}
        >
          <div className="flex items-center justify-center gap-2">
            {becomingOrganizer
              ? <Loader size={20} className="text-gray-600 dark:text-gray-400 animate-spin" />
              : <Star size={20} className="text-emerald-600 dark:text-emerald-400" />
            }
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">
              {becomingOrganizer ? 'Активация...' : 'Стать организатором'}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Приглашайте друзей и получайте бонусы за каждого
          </div>
        </button>
      )}
      {/* Выход из аккаунта */}
      {Platform.isStandalone() && (
        <button
          onClick={() => {
            if (confirm('Выйти из аккаунта?')) {
              AuthManager.clearTokens();
              Platform.clearUser();
              sessionStorage.clear();
              window.location.href = '/login';
            }
          }}
          className="w-full mt-6 py-3 rounded-2xl text-red-500 dark:text-red-400 text-sm font-medium flex items-center justify-center gap-2 transition-all active:scale-[0.98] hover:scale-[1.01]"
          style={{
            background: 'rgba(239, 68, 68, 0.06)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 1px rgba(239,68,68,0.08), 0 2px 8px rgba(239,68,68,0.06)',
            border: '1px solid rgba(239, 68, 68, 0.15)'
          }}
        >
          <LogOut size={16} />
          Выйти из аккаунта
        </button>
      )}

      </div>{/* закрытие .px-4 контента */}

      {/* Модалка деталей заказа */}
      <OrderDetailModal
        order={selectedOrder}
        isOpen={orderModalOpen}
        onClose={() => { setOrderModalOpen(false); setSelectedOrder(null); }}
        onOrderUpdated={async () => {
          try {
            const response = await botAPI.getUserOrders();
            if (response?.orders) setOrders(response.orders);
          } catch (e) {
            logger.log('Error refreshing orders:', e);
          }
        }}
      />
    </div>
  );
};

export default ProfilePage;
